import { Component, OnInit, AfterViewInit, OnDestroy, HostListener, ViewChild, ElementRef } from '@angular/core';
import { UntypedFormControl, UntypedFormBuilder, Validators, UntypedFormGroup, UntypedFormArray, FormArray, FormGroup } from '@angular/forms';
import { BaseComponent } from '../base-component';
import { CustomValidatorsService } from '../service/custom-validators.service';
import { AuthService } from '../service/auth.service';
import { EventService } from '../service/event.service';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AfPcDomain } from '../model/af.model';
import { ValidateAndNavigateEvent, ProgressBarEvent, ChangeNavigationEvent, SkipToMainContentEvent, ShowProgressEvent } from '../service/events';
import { KeyValue, DatePipe } from '@angular/common';
import { PoliticalContributionService } from '../service/political-contribution.service';

@Component({
  selector: 'app-pc-survey',
  templateUrl: './pc-survey.component.html',
  styleUrls: ['./pc-survey.component.scss'],
  standalone: false
})
export class PcSurveyComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

  selected = new UntypedFormControl(0);
  contributionData: UntypedFormGroup;
  politicalContributionGroup: UntypedFormGroup;
  pcSurveyData: AfPcDomain;
  signatureImg: string; //for @Input wet-signature
  sigCheckValue: boolean = false;
  date: any;
  contributionListChange: boolean = false;
  check: boolean = false;
  checkContribution: boolean = false;
  checkRadio: boolean = false;
  checkRadioTwo: boolean = false;
  blueBorder: boolean = false;
  redBorder: boolean = false;

  // Regex to match Server Validations
  regexName: RegExp = /^[A-Za-z\u00C0-\u017F\-',. ]+$/;
  regexJobPosition: RegExp = /^$|^[a-zA-Z0-9\s\-&().,'"/\\:;]+$/;
  regexAmount: RegExp = /^[a-zA-Z0-9\s\-&().,'"/\\:;$]+$/;
  regexSubdivision: RegExp = /^[a-zA-Z0-9\s.#\-/*@&"'():;]+$/;

  @ViewChild("missingElement") missingElement: ElementRef;
  @ViewChild("addElement") addElement: ElementRef;
  @ViewChild("addButtonElement") addButtonElement: ElementRef;
  @ViewChild("addLocalElement") addLocalElement: ElementRef;
  @ViewChild("addLocalButtonElement") addLocalButtonElement: ElementRef;
  @ViewChild("addPacsElement") addPacsElement: ElementRef;
  @ViewChild("addPacsButtonElement") addPacsButtonElement: ElementRef;

  constructor(private readonly eventBus: EventService, private readonly _router: Router, private readonly fb: UntypedFormBuilder, private readonly userService: AuthService,
    private readonly datepipe: DatePipe, private readonly customValidator: CustomValidatorsService, private readonly _snackBar: MatSnackBar, private readonly politicalService: PoliticalContributionService) {
    super(eventBus, _router);
  }

  ngOnInit() {
    this.createGroup();
    this.subscribeEvents();
  }

  subscribeEvents() {
    this.politicalService.getPcSurvey().subscribe({
      next: (data: AfPcDomain) => {
        this.pcSurveyData = data;
        this.politicalContributionGroup.patchValue(data);
        this.signatureImg = data.pcGwimSignature;
        this.date = data.pcGwimWetSignatureDate;
        for (let afPcDetail of this.pcSurveyData.pcLocalPartiesList) {
          let fg: UntypedFormGroup = this.fieldsGroup();
          fg.patchValue(afPcDetail);
          this.pcLocalPartiesList.push(fg);
        }

        for (let afPcDetail of this.pcSurveyData.pcPacsList) {
          let fg: UntypedFormGroup = this.fieldsGroup();
          fg.patchValue(afPcDetail);
          this.pcPacsList.push(fg);
        }

        for (let afPcDetail of this.pcSurveyData.pcCandidateList) {
          let fg: UntypedFormGroup = this.fieldsGroup();
          fg.patchValue(afPcDetail);
          this.pcCandidateList.push(fg);
        }
      }
    });

    this.sub = this.eventBus.on(
      ValidateAndNavigateEvent,
      (event): void => {
        this.bookmarkUrl.setValue("");
        if (event.payload.currentLink === '/pc-survey') {
          this.bookmarkUrl.setValue("");
          if (event.payload.save) {
            this.submitPcGwim((this.politicalContributionGroup.value), event);
          } else if (event.payload.saveAndFinishLater) {
            this.bookmarkUrl.setValue(event.payload.currentLink);
            this.submitPcGwim((this.politicalContributionGroup.value), event);
          } else if (this.validateContribution() && this.validate()) {
            this.submitPcGwim((this.politicalContributionGroup.value), event);
          }
        }
      }
    );

    this.eventBus.emit(new ProgressBarEvent({ id: 0 }));
  }

  getUrl(sig: string) {
    this.signatureImg = sig; // Gets the base64 String from wet-signature @Output
    this.politicalContributionGroup.get('pcGwimSignature').setValue(sig);
  }

  sigCheck(sigCheck: boolean) {
    this.sigCheckValue = sigCheck;
  }

  submitPcGwim(afPcDomain: AfPcDomain, event) {
    this.politicalService.savePcSurvey(afPcDomain).subscribe({
      next: () => {
        if (event.payload.save) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
        } else if (event.payload.saveAndFinishLater) {
          this._snackBar.open("Saved successfully", "Close", {
            duration: 4000,
          });
          this._router.navigate(['sessionTimedOut']);
        } else {
          this.eventBus.emit(new ChangeNavigationEvent({ id: event.payload.id }));
          this._router.navigateByUrl(event.payload.nextNavLink);
        }
      },
      error: (error: any) => console.log(error)
    });
  }

  fieldsGroup(): UntypedFormGroup {
    return this.fb.group({
      pcType: [''],
      recipientName: ['', [this.customValidator.validateRequiredField, Validators.maxLength(250), Validators.required, Validators.pattern(this.regexName)]],
      contributionDate: ['', [this.customValidator.validateRequiredField, Validators.required]],
      officeRunning: [''],
      amount: ['', [this.customValidator.validateRequiredField, Validators.required, Validators.pattern(this.regexAmount)]],
      address: [''],
      officeHeld: [''],
      subdivision: ['', [Validators.pattern(this.regexSubdivision)]],
      contributionType: ['']
    })
  }

  valueAscOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => {
    return a.value.localeCompare(b.value);
  }

  createCandidateContribution(check: string) {
    let fg: UntypedFormGroup = this.fieldsGroup();
    this.pcCandidateList.push(fg);
    this.checkContribution = false;

    setTimeout(() => {
      if (check == 'click') {
        this.addElement.nativeElement.focus();
      }
      this.blueBorder = true;
    }, 0);
  }

  createLocalPartyContribution(check: string) {
    let fg: UntypedFormGroup = this.fieldsGroup();
    this.pcLocalPartiesList.push(fg);
    this.checkContribution = false;

    setTimeout(() => {
      if (check == 'click') {
        this.addLocalElement.nativeElement.focus();
      }
      this.blueBorder = true;
    }, 0);
  }

  createPacsContribution(check: string) {
    let fg: UntypedFormGroup = this.fieldsGroup();
    this.pcPacsList.push(fg);
    this.checkContribution = false;

    setTimeout(() => {
      if (check == 'click') {
        this.addPacsElement.nativeElement.focus();
      }
      this.blueBorder = true;
    }, 0);
  }

  createGroup() {
    this.politicalContributionGroup = this.fb.group({
      verifyId: [this.userService.getVerifyId(), []],
      contribution: ['', Validators.required],
      pcCandidateList: new UntypedFormArray([]),
      pcLocalPartiesList: new UntypedFormArray([]),
      pcPacsList: new UntypedFormArray([]),
      gwimVoteForCandidate: ['', Validators.required],
      pcPosition: ['', [Validators.pattern(this.regexJobPosition)]],
      pcGwimSignature: ['', Validators.required],
      pcGwimAccepted: ['', Validators.requiredTrue],
      pcGwimSignedName: ['', [Validators.required, Validators.maxLength(250), this.customValidator.validateRequiredField, Validators.pattern(this.regexName)]],
      pcGwimWetSignatureDate: [''],
      submit: [false],
      bookmarkUrl: [''],
    });
  }

  get submit() { return this.politicalContributionGroup.get('submit') }
  get pcDetailsList() { return this.politicalContributionGroup.get('pcDetailsList') as UntypedFormArray; };
  get pcCandidateList() { return this.politicalContributionGroup.get('pcCandidateList') as UntypedFormArray; };
  get pcLocalPartiesList() { return this.politicalContributionGroup.get('pcLocalPartiesList') as UntypedFormArray; };
  get pcPacsList() { return this.politicalContributionGroup.get('pcPacsList') as UntypedFormArray; };
  get bookmarkUrl() { return this.politicalContributionGroup.get('bookmarkUrl') };
  get pcGwimAccepted() { return this.politicalContributionGroup.get('pcGwimAccepted') };
  get pcGwimSignedName() { return this.politicalContributionGroup.get('pcGwimSignedName') };
  get pcGwimSignature() { return this.politicalContributionGroup.get('pcGwimSignature') };
  get pcGwimWetSignatureDate() { return this.politicalContributionGroup.get('pcGwimWetSignatureDate') };
  get contribution() { return this.politicalContributionGroup.get('contribution') };
  get gwimVoteForCandidate() { return this.politicalContributionGroup.get('gwimVoteForCandidate') };

  focusTarget() {
    setTimeout(() => {
      this.missingElement.nativeElement.focus();
      this.redBorder = true;
    });
  }

  validateContribution() {
    if (this.contribution.value == 1 && this.pcCandidateList.length == 0 && this.pcLocalPartiesList.length == 0 && this.pcPacsList.length == 0) {
      this.focusTarget();
      this.checkContribution = true;
      this.eventBus.emit(new ShowProgressEvent({ show: false }));
      return false;
    }
    return true;
  }

  validate() {
    // Clear errors and lists if contribution is 0 and a User had selected Yes prior to No.
    if (this.contribution.value == 0) {
      this.politicalContributionGroup.markAsPristine();
      this.politicalContributionGroup.markAsUntouched();
      this.pcLocalPartiesList.clear();
      this.pcCandidateList.clear();
      this.pcPacsList.clear();
      this.politicalContributionGroup.updateValueAndValidity();
      return true;
    }

    if (!this.pcGwimAccepted.value) {
      this.pcGwimAccepted.setErrors({ invalid: true });
      this.check = true;
    }

    if (!this.pcGwimSignature.value) {
      this.pcGwimSignature.setErrors({ invalid: true });
    }

    this.submit.setValue(true);
    this.politicalContributionGroup.markAllAsTouched();
    if (this.politicalContributionGroup?.invalid) {
      let isInvalid = true;
      const firstInvalidField = Object.keys(this.politicalContributionGroup.controls).find(field => this.politicalContributionGroup.get(field)?.invalid);
      if (this.politicalContributionGroup.controls[firstInvalidField] instanceof FormArray) {
        (<FormArray>this.politicalContributionGroup.controls[firstInvalidField]).controls.forEach((data: FormGroup) => {
          if (data.invalid && isInvalid) {
            this.userService.setFocus(data);
            isInvalid = false;
          }
        })
      } else {
        this.userService.setFocus(this.politicalContributionGroup);
      }
    }
    return !this.politicalContributionGroup.invalid;
  }

  confirmOnBlur() {
    if (this.pcGwimAccepted.value) {
      this.check = false;
    } else {
      this.check = true;
    }
  }

  confirmOnFocus() {
    this.check = false;
  }

  ngAfterViewInit() {
    this.politicalContributionGroup.valueChanges.subscribe(() => {
      setTimeout(() => {
        let progress = this.getPercentage(this.politicalContributionGroup);
        this.eventBus.emit(new ProgressBarEvent({ id: progress }));
      }, 100);
    });

    this.pcGwimAccepted.valueChanges.subscribe(() => {
      this.setDate();
    });

    // retain navigation on page refresh
    let navlink = this.userService.getNavLinks().find(e => e.routerLink === "/pc-survey");
    this.eventBus.emit(new ChangeNavigationEvent({ id: navlink.id }));

    this.handleZoom();
  }

  setDate() {
    if (this.pcGwimAccepted.value) {
      if (!this.date) {
        if (!this.pcSurveyData.pcGwimWetSignatureDate) {
          this.date = this.datepipe.transform(new Date(), 'MM/dd/yyyy HH:mm:ss');
        }
      }
    } else if (!this.pcSurveyData.pcGwimWetSignatureDate) {
      this.date = null;
    }
  }

  deletePacsContribution(i) {
    this.pcPacsList.removeAt(i);

    setTimeout(() => {
      if (this.pcPacsList && this.pcPacsList.length == 0) {
        this.addPacsButtonElement.nativeElement.focus();
        this.blueBorder = false;
      } else {
        this.addPacsElement.nativeElement.focus();
        this.blueBorder = true;
      }
    }, 0);
  }

  deleteLocalPartyContribution(i) {
    this.pcLocalPartiesList.removeAt(i);

    setTimeout(() => {
      if (this.pcLocalPartiesList && this.pcLocalPartiesList.length == 0) {
        this.addLocalButtonElement.nativeElement.focus();
        this.blueBorder = false;
      } else {
        this.addLocalElement.nativeElement.focus();
        this.blueBorder = true;
      }
    }, 0);
  }

  deleteCandidateContribution(i) {
    this.pcCandidateList.removeAt(i);

    setTimeout(() => {
      if (this.pcCandidateList && this.pcCandidateList.length == 0) {
        this.addButtonElement.nativeElement.focus();
        this.blueBorder = false;
      } else {
        this.addElement.nativeElement.focus();
        this.blueBorder = true;
      }
    }, 0);
  }

  getPercentage(politicalContributionGroup: UntypedFormGroup): number {
    if (this.contribution.value == '0') {
      return 100;
    }

    let container: UntypedFormGroup = politicalContributionGroup;
    let errorCount = 0;
    let totalCount = 0;

    for (let controlKey in container.controls) {
      if (container.controls.hasOwnProperty(controlKey)) {
        if (container.controls[controlKey].validator != null) {
          totalCount += container.controls[controlKey].validator.length;
        }
        if (container.controls[controlKey].errors) {
          errorCount += Object.keys(container.controls[controlKey].errors).length;
        }
      }
    }

    for (let container of politicalContributionGroup.get('pcPacsList')['controls']) {
      for (let controlKey in container.controls) {
        if (container.controls.hasOwnProperty(controlKey)) {
          if (container.controls[controlKey].validator != null) {
            totalCount += container.controls[controlKey].validator.length;
          }
          if (container.controls[controlKey].errors) {
            errorCount += Object.keys(container.controls[controlKey].errors).length;
          }
        }
      }
    }

    for (let container of politicalContributionGroup.get('pcCandidateList')['controls']) {
      for (let controlKey in container.controls) {
        if (container.controls.hasOwnProperty(controlKey)) {
          if (container.controls[controlKey].validator != null) {
            totalCount += container.controls[controlKey].validator.length;
          }
          if (container.controls[controlKey].errors) {
            errorCount += Object.keys(container.controls[controlKey].errors).length;
          }
        }
      }
    }

    for (let container of politicalContributionGroup.get('pcLocalPartiesList')['controls']) {
      for (let controlKey in container.controls) {
        if (container.controls.hasOwnProperty(controlKey)) {
          if (container.controls[controlKey].validator != null) {
            totalCount += container.controls[controlKey].validator.length;
          }
          if (container.controls[controlKey].errors) {
            errorCount += Object.keys(container.controls[controlKey].errors).length;
          }
        }
      }
    }
    let percentage = ((totalCount - errorCount) / totalCount) * 100;
    return percentage;
  }

  onFocusOutMainContent() {
    this.eventBus.emit(new SkipToMainContentEvent({ text: "FocusOff" }));
  }

  radioOnBlur() {
    if (this.contribution.value) {
      this.checkRadio = false;
    } else if (this.contribution.value === 0) {
      this.checkRadio = false;
    } else {
      this.checkRadio = true;
    }
  }

  radioOnFocus() {
    this.checkRadio = false;
  }

  radioTwoOnBlur() {
    if (this.gwimVoteForCandidate.value) {
      this.checkRadioTwo = false;
    } else if (this.gwimVoteForCandidate.value === 0) {
      this.checkRadioTwo = false;
    } else {
      this.checkRadioTwo = true;
    }
  }

  radioTwoOnFocus() {
    this.checkRadioTwo = false;
  }

  dropDownScreenReaderElectionType(election: any): string {
    if (election.controls.contributionType.value) {
      return 'Selected ' + this.pcSurveyData.pcElectionTypeLookup[Object.keys(this.pcSurveyData.pcElectionTypeLookup).find(e => e === election.controls.contributionType.value)];
    } else {
      return 'blank';
    }
  }

  dropDownScreenReaderOffice(election: any): string {
    if (election.controls.officeHeld.value) {
      return 'Selected ' + this.pcSurveyData.pcOfficeLookup[Object.keys(this.pcSurveyData.pcOfficeLookup).find(e => e === election.controls.officeHeld.value)];
    } else {
      return 'blank';
    }
  }

  dropDownScreenReaderRunning(election: any): string {
    if (election.controls.officeRunning.value) {
      return 'Selected ' + this.pcSurveyData.pcOfficeLookup[Object.keys(this.pcSurveyData.pcOfficeLookup).find(e => e === election.controls.officeRunning.value)];
    } else {
      return 'blank';
    }
  }

  @HostListener('window:resize')
  @HostListener('window:load')
  handleZoom() {
    const windowWidth = window.innerWidth;
    if (windowWidth <= 975) {
      this.contributionListChange = true;
    } else {
      this.contributionListChange = false;
    }
  }

  @HostListener('document:keydown', ['$event'])
  clearBorder(keyDown: KeyboardEvent) {
    if (keyDown.key === 'Tab') {
      this.blueBorder = false;
      this.redBorder = false;
    }
  }

  @HostListener('click')
  clearBorderClick() {
    this.blueBorder = false;
    this.redBorder = false;
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
